import { useId, useState } from 'react';
import { Dialog as DialogPrimitive } from 'radix-ui';

import { cn } from '@april9au/react-common/utils';

import type { ImageField, KeyTextField, RichTextField } from '@prismicio/client';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { PrismicMedia } from '@/components/ui/media';
import { useStrings } from '@/i18n/use-strings';
import { hasText, plainTextOr, RichText } from './rich-text';

/**
 * A video in the thread. The card is the message; the dialog is the player.
 *
 * The video is a **gate**: the chat does not move on until the learner presses
 * Continue inside the dialog, exactly as the current app behaves.
 *
 * The transcript is reachable from *both* surfaces, because it is the
 * alternative to the video rather than a footnote about it: collapsed under
 * the card, so the module is usable with the sound off or with no video at
 * all, and collapsed inside the player, so a learner who is already watching
 * never has to close the thing they are watching to read along. The two
 * disclosures keep their own state — expanding one is not a claim about the
 * other.
 */

export type VideoMessageProps = {
  title: KeyTextField;
  subtitle: KeyTextField;
  poster: ImageField;
  embedLink: KeyTextField;
  transcript: RichTextField;
  /** Present while this video is the gate; absent once it has been passed. */
  onContinue?: () => void;
  /**
   * Controlled player state. The gating video is opened from the answer footer
   * as well as from its own card — a learner who dismissed the player must not
   * have to hunt back up the thread for it — so the chat owns that one flag.
   * Every other video keeps its own.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

type Provider = 'vimeo' | 'youtube' | 'brightcove' | 'unknown' | 'invalid';

/**
 * Which player an `embed_link` points at. Staff occasionally paste a whole
 * `<iframe>` tag; that is called out rather than rendered, which is how the
 * current app behaves and the only way an editor finds out.
 */
export function videoProvider(embedLink: KeyTextField): Provider {
  const link = embedLink ?? '';

  if (link.includes('<iframe')) {
    return 'invalid';
  }

  if (link.includes('vimeo.com')) {
    return 'vimeo';
  }

  if (link.includes('youtube.com') || link.includes('youtu.be')) {
    return 'youtube';
  }

  if (link.includes('brightcove.net')) {
    return 'brightcove';
  }

  return 'unknown';
}

/** The embed URL with `autoplay=1`, or `null` when the link will not parse. */
export function autoplayUrl(embedLink: KeyTextField): string | null {
  if (embedLink === null || embedLink.trim() === '') {
    return null;
  }

  try {
    const url = new URL(embedLink);

    url.searchParams.set('autoplay', '1');

    return url.toString();
  } catch {
    return null;
  }
}

const PLAYER_ALLOW =
  'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen';

/**
 * The disclosure that opens a transcript. One component for both surfaces so
 * the label, the chevron and the `aria-expanded`/`aria-controls` wiring cannot
 * drift apart; `tone` is the only thing that differs, because the card sits on
 * white and the player sits on navy.
 */
function TranscriptToggle({
  shown,
  onToggle,
  controls,
  tone,
  className,
}: {
  shown: boolean;
  onToggle: () => void;
  controls: string;
  tone: 'card' | 'player';
  className?: string;
}) {
  const { chat } = useStrings();

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={shown}
      aria-controls={controls}
      className={cn(
        'flex min-h-11 items-center gap-1.5 rounded-lg text-xs font-bold focus-visible:ring-[3px] focus-visible:outline-none',
        tone === 'card' && 'text-surf-blue focus-visible:ring-surf-blue/40 -mx-2 px-2',
        tone === 'player' &&
          'justify-center border border-white/25 px-4 text-sm text-white hover:bg-white/10 focus-visible:ring-white/50',
        className,
      )}
    >
      <Icon
        name="passport/chevron-down"
        aria-hidden
        className={cn('size-4 transition-transform duration-200 motion-reduce:transition-none', shown && 'rotate-180')}
      />
      {shown ? chat.chat_transcript_hide : chat.chat_transcript_show}
    </button>
  );
}

export function VideoMessage({
  title,
  subtitle,
  poster,
  embedLink,
  transcript,
  onContinue,
  open: openProp,
  onOpenChange,
}: VideoMessageProps) {
  const { chat } = useStrings();
  // Uncontrolled by default; `openProp`/`onOpenChange` take over for the gate.
  const [selfOpen, setSelfOpen] = useState(false);
  const open = openProp ?? selfOpen;
  const setOpen = onOpenChange ?? setSelfOpen;
  const [cardTranscript, setCardTranscript] = useState(false);
  const [playerTranscript, setPlayerTranscript] = useState(false);
  const panelId = useId();

  const provider = videoProvider(embedLink);
  const src = autoplayUrl(embedLink);
  const heading = plainTextOr(title, chat.chat_play_video);
  const transcribed = hasText(transcript);

  return (
    <div className="bg-bubble-buddy w-full max-w-[85%] overflow-hidden rounded-2xl rounded-es-md shadow-[0_1px_2px_rgb(16_24_64/0.06),0_4px_12px_-6px_rgb(16_24_64/0.14)]">
      <button
        type="button"
        // A module can hold several videos, and only the one the chat is
        // waiting on carries Continue. The hook says which is which, so the
        // Autopilot opens the gate rather than a video already passed.
        data-autopilot={onContinue === undefined ? 'video-play' : 'video-play-gate'}
        onClick={() => setOpen(true)}
        aria-label={`${chat.chat_play_video}: ${heading}`}
        className="chat-pressable focus-visible:ring-surf-blue/40 group/play relative block aspect-video w-full focus-visible:ring-[3px] focus-visible:outline-none"
      >
        {poster.url === null ? (
          <span className="bg-surf-dark-blue block size-full" />
        ) : (
          <PrismicMedia
            image={poster}
            alt=""
            placeholder="var(--color-surf-dark-blue)"
            className="size-full object-cover"
          />
        )}
        <span className="absolute inset-0 grid place-items-center bg-black/15">
          {/* SLSF red is the chat's primary action colour, play included. */}
          <span className="bg-surf-red grid size-14 place-items-center rounded-full text-white shadow-lg transition-transform duration-150 group-hover/play:scale-105">
            {/* Nudged right: a play triangle centred geometrically reads left. */}
            <Icon name="passport/play" className="size-6 translate-x-[1px]" />
          </span>
        </span>
      </button>

      <div className="px-4 py-3">
        <p className="text-chat-ink text-sm font-bold">{heading}</p>
        {subtitle === null || subtitle.trim() === '' ? null : (
          <p className="text-chat-ink-muted mt-0.5 text-xs font-semibold">{subtitle}</p>
        )}

        {transcribed ? (
          <div className="mt-3">
            <TranscriptToggle
              shown={cardTranscript}
              onToggle={() => setCardTranscript((shown) => !shown)}
              controls={`${panelId}-card`}
              tone="card"
            />
            {cardTranscript ? (
              <div
                id={`${panelId}-card`}
                className="border-chat-hairline text-chat-ink mt-1 border-t pt-3 text-sm leading-relaxed"
              >
                <RichText field={transcript} />
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/85 duration-[var(--bp-duration-base)] ease-[var(--bp-ease-out)]" />
          <DialogPrimitive.Content
            data-slot="chat-video-player"
            className={cn(
              'bg-surf-dark-blue data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 fixed inset-0 z-50 flex flex-col',
              'duration-[var(--bp-duration-base)] ease-[var(--bp-ease-out)] data-[state=closed]:duration-[var(--bp-duration-fast)]',
              // Paints under the cutout and the home indicator, then puts both
              // back — a full-screen player is a fixed edge like the sheets.
              'pt-[env(safe-area-inset-top,0px)] pb-[env(safe-area-inset-bottom,0px)]',
              // Fades in place under reduced motion — the shared rule in
              // `app.css`, which needs the `data-slot` above to reach this.
              'bp-overlay',
            )}
          >
            <div className="flex min-h-14 shrink-0 items-center justify-between gap-3 px-4">
              {/* Visible, not a screen-reader afterthought: a learner who has
                  opened three videos needs to know which one this is. */}
              <DialogPrimitive.Title className="min-w-0 truncate text-sm font-bold text-white">
                {heading}
              </DialogPrimitive.Title>
              <DialogPrimitive.Close asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={chat.chat_image_close}
                  className="size-11 shrink-0 text-white hover:bg-white/15 active:bg-white/25"
                >
                  <Icon name="passport/close" aria-hidden className="size-5" />
                </Button>
              </DialogPrimitive.Close>
            </div>

            {/* The dialog's description says what else is in here, rather than
                repeating the title the row above already carries. */}
            <DialogPrimitive.Description className="sr-only">
              {transcribed ? chat.chat_transcript_available : heading}
            </DialogPrimitive.Description>

            <div className="grid min-h-0 flex-1 place-items-center px-4">
              {/*
                The 16:9 box is reserved by the *wrapper*, and the player is
                absolutely positioned inside it. `aspect-video` on the iframe
                itself fought `max-h-full`, so the box the player landed in was
                not the box the layout had measured and the frame jumped once
                the embed mounted. Now the geometry is settled before the
                provider has been asked for anything.
              */}
              {/* `max-h-full` is the one thing the box needs beyond its
                  reserved ratio: with the transcript open on a short window
                  the ratio would otherwise put the player past the panel
                  below it. Capped, the frame letterboxes inside a box it
                  already owns rather than overflowing one it does not. */}
              <div className="relative max-h-full w-full max-w-3xl overflow-hidden rounded-xl bg-black portrait:h-full landscape:aspect-video lg:max-w-5xl">
                {provider === 'invalid' || src === null ? (
                  // One line for the learner either way: an unkeyed embed link
                  // and a pasted `<iframe>` are both "not available yet" from
                  // where they are sitting. The distinction an editor needs is
                  // logged instead of rendered.
                  <p className="absolute inset-0 grid place-items-center p-4 text-center text-sm font-semibold text-white">
                    {chat.chat_video_unavailable}
                  </p>
                ) : (
                  <iframe
                    src={src}
                    title={heading}
                    allow={PLAYER_ALLOW}
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin"
                    className="absolute inset-0 size-full border-0"
                  />
                )}
              </div>
            </div>

            {/*
              The transcript, on the surface it belongs to. A solid white
              document panel on the navy chrome, capped at 45 % of the dialog's
              height and scrolling inside itself: the video stays on screen and
              keeps playing, so reading along is possible rather than a swap.
              The cap, rather than a share of the free space, is what keeps a
              long transcript from pushing the footer off the bottom — the
              player above it takes whatever is left.
            */}
            {transcribed && playerTranscript ? (
              <div
                id={`${panelId}-player`}
                className="mx-auto flex max-h-[45%] w-full max-w-3xl shrink-0 flex-col overflow-hidden rounded-2xl bg-white ps-4 pe-2 pt-3 lg:max-w-5xl"
              >
                <h3 className="text-chat-ink-muted shrink-0 text-[11px] font-bold tracking-[0.12em] uppercase">
                  {chat.chat_transcript_heading}
                </h3>
                <div className="text-chat-ink min-h-0 flex-1 overflow-y-auto overscroll-contain pe-2 pt-2 pb-4 text-sm leading-relaxed">
                  <RichText field={transcript} />
                </div>
              </div>
            ) : null}

            {/*
              The thumb zone, and it is never empty: the gate's Continue lives
              here, and a video that is not gating gets a full-width Close.
              Both are shown at *every* width. An earlier pass hid the Close
              from `sm` up on the grounds that the top-right × was reachable on
              a desktop, which left a transcribed video showing a bar with
              nothing in it but the disclosure — a dialog whose only way out
              was a 44 px icon in the far corner. The way on is a real button,
              on every screen. The transcript disclosure sits above whichever
              of the two it is, because that is where a learner looks for it.
            */}
            <div className="mx-auto flex w-full max-w-3xl shrink-0 flex-col gap-2 px-4 pt-3 pb-4 lg:max-w-5xl">
              {transcribed ? (
                <TranscriptToggle
                  shown={playerTranscript}
                  onToggle={() => setPlayerTranscript((shown) => !shown)}
                  controls={`${panelId}-player`}
                  tone="player"
                />
              ) : null}

              {onContinue ? (
                /* Red is the chat's primary action colour, but `--surf-red`
                   is only 4.30:1 under white; `--bp-destructive` is the same
                   hue that clears 4.5:1 (ticket 23 §3). */
                <Button
                  size="xl"
                  data-autopilot="video-continue"
                  className="bg-bp-destructive hover:bg-bp-destructive-hover active:bg-bp-destructive-pressed focus-visible:ring-bp-destructive/40 text-white"
                  onClick={() => {
                    setOpen(false);
                    onContinue();
                  }}
                >
                  {chat.chat_video_continue}
                </Button>
              ) : (
                <DialogPrimitive.Close asChild>
                  <Button
                    size="xl"
                    variant="ghost"
                    className="border border-white/30 bg-white/10 text-white hover:bg-white/20 active:bg-white/25"
                  >
                    {chat.chat_image_close}
                  </Button>
                </DialogPrimitive.Close>
              )}
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </div>
  );
}
